import React, { useState, useEffect } from 'react';
import '../assets/styles/NewsletterPopup.css';

interface NewsletterPopupProps {
  // Delay before showing popup in milliseconds (default: 5000 - 5 seconds)
  delay?: number;
  // Whether to show the popup only once per session (default: true)
  showOncePerSession?: boolean;
}

// Cookie helper functions
const setCookie = (name: string, value: string, days: number) => {
  const date = new Date();
  date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
  const expires = "expires=" + date.toUTCString();
  document.cookie = name + "=" + value + ";" + expires + ";path=/";
};

const getCookie = (name: string): string | null => {
  const cookieName = name + "=";
  const decodedCookie = decodeURIComponent(document.cookie);
  const cookieArray = decodedCookie.split(';');
  
  for (let i = 0; i < cookieArray.length; i++) {
    let cookie = cookieArray[i];
    while (cookie.charAt(0) === ' ') {
      cookie = cookie.substring(1);
    }
    if (cookie.indexOf(cookieName) === 0) {
      return cookie.substring(cookieName.length, cookie.length);
    }
  }
  return null;
};

const NewsletterPopup: React.FC<NewsletterPopupProps> = ({
  delay = 5000,
  showOncePerSession = true,
}) => {
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [name, setName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [phone, setPhone] = useState<string>('');
  const [status, setStatus] = useState<{
    loading: boolean;
    success: boolean;
    error: string | null;
  }>({
    loading: false,
    success: false,
    error: null,
  });

  useEffect(() => {
    // For development - uncomment to reset session storage and cookies when testing
    // sessionStorage.removeItem('newsletter_popup_shown');
    // document.cookie = "newsletter_subscribed=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    console.log("NewsletterPopup mounted, will display after", delay, "ms");
    console.log("ShowOncePerSession:", showOncePerSession);
    console.log("Session storage flag:", sessionStorage.getItem('newsletter_popup_shown'));
    console.log("Newsletter cookie:", getCookie('newsletter_subscribed'));
    
    // Check if user has already subscribed (via cookie)
    if (getCookie('newsletter_subscribed')) {
      console.log("User already subscribed, not showing popup");
      return;
    }
    
    // Check if we should show the popup based on session storage (if showOncePerSession is true)
    if (showOncePerSession && sessionStorage.getItem('newsletter_popup_shown')) {
      console.log("Popup already shown this session, not showing again");
      return;
    }

    // Set a timer to show the popup after specified delay
    const timer = setTimeout(() => {
      console.log("Timer elapsed, showing popup");
      setIsVisible(true);
      if (showOncePerSession) {
        sessionStorage.setItem('newsletter_popup_shown', 'true');
      }
    }, delay);

    // Clean up the timer
    return () => {
      console.log("Cleaning up timer");
      clearTimeout(timer);
    };
  }, [delay, showOncePerSession]);

  const handleClose = () => {
    console.log("Closing popup");
    setIsVisible(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log("Form submitted");
    
    if (!email) {
      setStatus({
        loading: false,
        success: false,
        error: 'Email is required',
      });
      return;
    }

    setStatus({
      loading: true,
      success: false,
      error: null,
    });

    try {
      // Convert form data to URL encoded format
      const formData = new URLSearchParams();
      if (name) formData.append('name', name);
      formData.append('email', email);
      if (phone) formData.append('phone', phone);
      //formData.append('newsletter', 'true');
      
      console.log("Sending data to API:", Object.fromEntries(formData));
      
      // Send data to the API
      const response = await fetch('https://api.trader.studio/newsletter/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString()
      });
      
      console.log("API response status:", response.status);
      
      if (response.ok) {
        setStatus({
          loading: false,
          success: true,
          error: null,
        });
        
        // Set a persistent cookie that lasts for 1 year
        setCookie('newsletter_subscribed', 'true', 365);
        console.log("Set newsletter_subscribed cookie for 1 year");
        
        // Reset form
        setName('');
        setEmail('');
        setPhone('');
        
        // Auto-close after success
        setTimeout(() => {
          handleClose();
        }, 3000);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      
      setStatus({
        loading: false,
        success: false,
        error: error instanceof Error ? error.message : 'An unexpected error occurred.',
      });
    }
  };

  console.log("Popup visibility state:", isVisible);

  if (!isVisible) {
    return null;
  }

  return (
    <div className="newsletter-popup-overlay">
      <div className="newsletter-popup">
        <button className="newsletter-popup-close" onClick={handleClose}>
          &times;
        </button>
        
        <div className="newsletter-popup-content">
          <h3>Get AI Trading Insights!</h3>
          <p>
            Join our <strong>FREE</strong> newsletter and get exclusive deep-dive analysis reports, and expert market insights.
          </p>
          
          {status.success ? (
            <div className="newsletter-success">
              <div className="success-icon">✓</div>
              <p>Thank you for subscribing! We'll be in touch soon.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="compact-form">
              {status.error && (
                <div className="newsletter-error">{status.error}</div>
              )}
              
              <div className="form-group">
                <input
                  type="text"
                  id="newsletter-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name (optional)"
                />
              </div>
              
              <div className="form-group">
                <input
                  type="email"
                  id="newsletter-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email address*"
                  required
                />
              </div>
              
              <div className="form-group">
                <input
                  type="tel"
                  id="newsletter-phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="Phone (optional)"
                />
              </div>
              
              <div className="form-group">
                <button 
                  type="submit" 
                  className="subscribe-button"
                  disabled={status.loading}
                >
                  {status.loading ? 'Subscribing...' : 'Subscribe'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewsletterPopup; 